import Care from '@/types/Care'
import Caregiver from '@/types/Caregiver'
import CareType from '@/types/CareType'
import careTypes, {careTypeOther} from '@/constants/careTypes'
import firebase from './firebase'
import moment from 'moment'

type CareData = any

interface CareDocDataOut {
  careTypeKey: string,
  careData: CareData
  caredAt: firebase.firestore.FieldValue | firebase.firestore.Timestamp
  createdAt: firebase.firestore.FieldValue
  caregiverUserId: string
  isPending: boolean
}

interface CareDocDataIn {
  careTypeKey: string,
  careData: CareData
  caredAt: firebase.firestore.Timestamp
  createdAt?: firebase.firestore.Timestamp
  caregiverUserId: string
  isPending: boolean
}

interface CareQueryOption {
  caredAtFrom?: moment.Moment
  caredAtTo?: moment.Moment
  isPending?: boolean
  careType?: string
  limit?: number
}

export default class {
  newCare(careType: CareType, childId: string): Care {
    const careData: any = careType.fields.reduce((o, field) => ({
      ...o,
      [field.key]: field.default || null,
    }), {})
    return {
      careType,
      careData,
      childId,
      id: '',
      caredAt: moment(),
      createdAt: moment(),
      isPending: false,
      isNew: true,
    }
  }

  listenNewCares(cb: Function, childId: string): Function {
    const baseCares = careTypes.map(careType => this.newCare(careType, childId))
    cb(baseCares)

    return this.getCaresRealtime((pendingCares)=>{
      const mergedCares = baseCares.map((baseCare) =>{
        if (baseCare.careType.hasEndForm) {
          const peidingCare = pendingCares.find(care => care.careType === baseCare.careType)
          if (peidingCare) {
            peidingCare.isNew = true
            return peidingCare
          }
          return baseCare
        }
        return baseCare
      })
      cb(mergedCares)
    }, childId, {isPending: true})

  }

  getUserId(): string {
    const currentUser = firebase.auth().currentUser
    if (!currentUser) throw new Error('required auth')
    return currentUser.uid
  }

  getCaresCollection(childId: string) {
    if (!childId) throw Error('childId required')
    return firebase.firestore().collection(`children/${childId}/cares`)
  }

  removeCare(childId: string, careId: string): Promise<void> {
    return this.getCaresCollection(childId).doc(careId).delete()
  }

  async createCare(childId: string, care: Care) {
    const careData: CareDocDataOut = {
      caredAt: firebase.firestore.FieldValue.serverTimestamp(),
      careData: care.careData,
      caregiverUserId: this.getUserId(),
      careTypeKey: care.careType.key,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      isPending: care.isPending,
    }
    await this.getCaresCollection(childId).add(careData)
  }

  docToCare(doc: firebase.firestore.DocumentData, childId: string): Care | null {
    const data: CareDocDataIn = doc.data()
    if (!data.createdAt) return null
    return {
      careType: careTypes.find(ct => ct.key === data.careTypeKey) || careTypeOther,
      careData: data.careData,
      childId,
      id: doc.id,
      caredAt: moment(data.caredAt.toDate()),
      createdAt: moment(data.createdAt.toDate()),
      isPending: data.isPending,
      isNew: false,
    }
  }

  async updateCare(childId: string, care: Care) {
    const careData: Partial<CareDocDataOut> = {
      caredAt: firebase.firestore.Timestamp.fromDate(care.caredAt.toDate()),
      isPending: care.isPending,
      careData: care.careData,
    }
    await this.getCaresCollection(childId).doc(care.id).set(careData, { merge: true })
  }

  snapshotToCares(ss: firebase.firestore.QuerySnapshot, childId: string): Care[] {
    const cares: Care[] = []
    ss.forEach(doc => {
      try {
        const care = this.docToCare(doc, childId)
        if (care) cares.push(care)
      } catch(_) {}
    })
    return cares
  }

  createQuery(childId: string, queryOption: CareQueryOption): firebase.firestore.Query {
    const toFirestoreTimestamp = (m: moment.Moment): firebase.firestore.Timestamp => {
      return firebase.firestore.Timestamp.fromDate(m.toDate())
    }
    let q: firebase.firestore.Query = this.getCaresCollection(childId)

    q = q.where('isPending', '==', !!queryOption.isPending)

    if (queryOption.careType) {
      q = q.where('careTypeKey', '==', queryOption.careType)
    }
    if (queryOption.caredAtFrom) {
      q = q.where('caredAt', '>=', toFirestoreTimestamp(queryOption.caredAtFrom))
    }
    if (queryOption.caredAtTo) {
      q = q.where('caredAt', '<=', toFirestoreTimestamp(queryOption.caredAtTo))
    }
    q = q.orderBy('caredAt', 'desc')
    if (queryOption.limit) {
      q = q.limit(queryOption.limit)
    }
    return q
  }

  async getCares(childId: string, queryOption: CareQueryOption): Promise<Care[]> {
    const ss = await this.createQuery(childId, queryOption).get()
    return this.snapshotToCares(ss, childId)
  }

  getCaresRealtime(
    callback: (cares: Care[]) => any,
    childId: string,
    queryOption: CareQueryOption,
  ): Function {
    const query = this.createQuery(childId, queryOption)
    return query.onSnapshot((ss: firebase.firestore.QuerySnapshot) => {
      callback(this.snapshotToCares(ss, childId))
    })
  }
}
